/** @jsx jsx */
import * as React from 'react';
import cx from 'classnames';
import {
  MenuInitialState,
  MenuSeparator,
  MenuItem,
  useMenuState,
  Menu,
  MenuItemProps,
} from 'reakit/Menu';
import { jsx, css } from '@emotion/core';
import { Button } from 'reakit/Button';
import DirectionBack from '@clearkit/icons/glyphs/DirectionBack';
import Checkmark from '@clearkit/icons/glyphs/Checkmark';

interface CKOptionMenuProps {
  className?: string;
  onChange: (value: string) => void;
  value: string;
  options?: MenuInitialState;
}

interface CKOptionMenuBackButtonProps {
  onClick: () => void;
}

interface CKActionMenuComposition {
  Item: React.FC<CKOptionMenuItemProps>;
  Header: React.FC;
  Heading: React.FC;
  Divider: React.FC;
  BackButton: React.FC<CKOptionMenuBackButtonProps>;
}

interface CKMenuDividerProps extends Partial<MenuItemProps> {
  onOptionChange?: (value: string) => void;
}

// @ts-ignore
export interface CKOptionMenuItemProps extends Partial<MenuItemProps> {
  children: React.ReactNode;
  isDisabled?: boolean;
  value: string;
  onOptionChange?: (value: string) => void;
  selected?: string;
}

export const CKOptionMenu: React.FC<CKOptionMenuProps> &
  CKActionMenuComposition = ({
  children,
  className,
  options,
  onChange,
  value,
  ...rest
}) => {
  const menuState = useMenuState(options);
  const menuClass = cx('ck-menu', 'ck-menu--options', className);
  return (
    <Menu
      className={menuClass}
      css={css`
        outline: 0;
        &:focus {
          outline: 0;
        }
      `}
      {...menuState}
      {...rest}
      visible={true}
    >
      {React.Children.map(children, child => {
        // @ts-ignore
        return React.cloneElement(child, {
          ...menuState,
          selected: value,
          onOptionChange: onChange,
        });
      })}
    </Menu>
  );
};

interface MenuItemInnerProps {
  isSelected?: boolean;
}

const MenuItemInner: React.FC<MenuItemInnerProps> = ({
  children,
  isSelected = false,
}) => {
  return (
    <React.Fragment>
      {isSelected && (
        <span className="ck-option-item-accessory">
          <Checkmark />
        </span>
      )}
      <span className="ck-option-value">{children}</span>
    </React.Fragment>
  );
};

const CKOptionMenuItem: React.FC<CKOptionMenuItemProps> = ({
  children,
  isDisabled,
  value,
  onOptionChange,
  ...rest
}) => {
  const isSelected = rest.selected === value;
  const itemClass = cx('ck-option-item', {
    'ck-option-item--selected': isSelected,
  });

  return (
    // @ts-ignore
    <MenuItem
      onClick={() => onOptionChange && onOptionChange(value)}
      disabled={isDisabled}
      className={itemClass}
      {...rest}
    >
      <MenuItemInner isSelected={isSelected} children={children} />
    </MenuItem>
  );
};

export const MenuHeader: React.FC = ({ children }) => {
  return <div className="ck-popover-header">{children}</div>;
};

const MenuBackButton: React.FC<CKOptionMenuBackButtonProps> = ({ onClick }) => {
  return (
    <Button onClick={onClick} className="ck-popover-back">
      <DirectionBack width={14} height={14} />
    </Button>
  );
};

const MenuHeading: React.FC = ({ children }) => {
  return <div className="ck-popover-header-title">{children}</div>;
};

const MenuDivider: React.FC<CKMenuDividerProps> = ({
  onOptionChange,
  ...rest
}) => <MenuSeparator className="ck-menu-divider" {...rest} />;

CKOptionMenu.Item = CKOptionMenuItem;
CKOptionMenu.Divider = MenuDivider;
CKOptionMenu.Header = MenuHeader;
CKOptionMenu.Heading = MenuHeading;
CKOptionMenu.BackButton = MenuBackButton;
